import React, {useState} from 'react';
import styled from 'styled-components';
import {Trans} from 'gatsby-plugin-react-i18next';
import {Content} from 'components/Content';
import {DownloadOnAppleStore} from './DownloadOnAppleStore';
import {DownloadOnGooglePlay} from './DownloadOnGooglePlay';

// Visibility is decided before first paint by the inline script in
// gatsby-ssr.js (`APP_BANNER_SCRIPT`), which adds `app-banner-ios` or
// `app-banner-android` to <html>. The banner is always in the static HTML and
// hidden by CSS otherwise, so it never shifts content in after hydration (CLS).
// Keep the class names and the localStorage key in sync with gatsby-ssr.js.
const DISMISSED_KEY = 'appDownloadBannerDismissed';

const Banner = styled.div`
  background-image: linear-gradient(-135deg, #aab5ea 0%, #41d7bd 100%);
  color: #ffffff;
  padding: 0.75rem 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  html.app-banner-ios &,
  html.app-banner-android & {
    display: block;
  }
`;

const BannerContent = styled(Content)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 0 1rem;
  }
`;

const Text = styled.p`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const IosBadge = styled.span`
  display: none;

  html.app-banner-ios & {
    display: inline;
  }
`;

const AndroidBadge = styled.span`
  display: none;

  html.app-banner-android & {
    display: inline;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  margin-left: 1rem;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  @media (max-width: 768px) {
    position: absolute;
    right: 1rem;
    top: 0.75rem;
  }
`;

export const AppDownloadBanner: React.FC = () => {
  const [isDismissed, setIsDismissed] = useState(false);

  const handleClose = () => {
    setIsDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } catch (error) {
      // If localStorage fails (e.g., SecurityError), silently fail
      // The banner is already hidden via setIsDismissed(true)
    }
  };

  if (isDismissed) {
    return null;
  }

  return (
    <Banner>
      <BannerContent>
        <Text>
          <Trans>Get the MONEI Pay app for easy payments on the go</Trans>
        </Text>
        <IosBadge>
          <DownloadOnAppleStore noMargin />
        </IosBadge>
        <AndroidBadge>
          <DownloadOnGooglePlay noMargin />
        </AndroidBadge>
        <CloseButton onClick={handleClose} aria-label="Close banner">
          ×
        </CloseButton>
      </BannerContent>
    </Banner>
  );
};
